import { useCallback } from 'react';
import {
  addAction,
  enterAction,
  enterXhrAction,
  enterXhrCallback,
  identifyUser,
  leaveAction,
  leaveXhrAction,
  leaveXhrCallback,
  reportError,
} from '@src/vendor/dynatrace';

/**
 * Do not instantiate directly, use the exported window.dtrum singleton instance
 */
export const useDynatrace = () => {
  /**
   * Convenience method to immediately enter and leave an action
   */
  const addActionCallback = useCallback(addAction, []);

  /**
   * Enters a new custom action. Use to set the load start event for a new custom action.
   * Needs to be called before leaveAction, which closes the custom action.
   *
   * @param actionName Name of the action
   * @param actionType DEPRECATED: not used any more and has no effect if provided
   * @param startTime  Timestamp in milliseconds. if null, current time is used.
   * @param sourceUrl  Source url for the action
   * @returns          ID of the created action or 0 if action was not created.
   */
  const enterActionCallback = useCallback(enterAction, []);

  /**
   * Leaves an action that has previously been created by an enterAction call.
   * Use to set the load end event for a custom action and to complete its creation.
   * Needs to be called after enterAction.
   *
   * @param actionId  ID of the action to leave. must be the value returned by enterAction
   * @param stopTime  Timestamp in milliseconds.
   *                  Note that, when providing a stop time, it will force stop the action and prevent visually complete from extending it.
   * @param startTime Optional start time in milliseconds (necessary if start time should be modified).
   *                  Note that, when providing a start time, it mustn't be longer than an hour in the past, otherwise the
   *                  RUM monitoring code will ignore it.
   */
  const leaveActionCallback = useCallback(leaveAction, []);

  /**
   * Extends or initiates actions.
   * Use when you want to extend an active Load or XHR action by another unlinked XHR call (i.e., action).
   * This is particularly useful when the XHR call in question is asynchronous in nature
   * and therefore can't automatically be correlated to an action, which otherwise
   * would lead to the action being closed too early and inaccurate metrics measurements (e.g., user action duration).
   * Needs to be called before leaveXhrAction.
   *
   * @param type   Optional additional info about type of XHR (e.g., framework name, etc.)
   * @param xmode  XHR action creation mode
   *               0 ... Just extend running XHR actions
   *               1 ... Extend any running action
   *               3 ... Start action if user input is present
   * @param xhrUrl url of the requested resource
   *               note that in the future, this argument will be required
   *               if not provided, the request will be marked as `/undefined` in the waterfall
   * @returns      ID of the XhrAction
   */
  const enterXhrActionCallback = useCallback(enterXhrAction, []);

  /**
   * Indicates the end of an XHR action
   *
   * @param actionId   ID of the XHR Action
   * @param [stopTime] The stop time of the XHR Action
   */
  const leaveXhrActionCallback = useCallback(leaveXhrAction, []);

  /**
   * Indicates that an XHR callback is active (e.g. XMLHttpRequest onreadystatechange) and relinks subsequently triggered XHR actions.
   * For example, when an XHR callback adds a script tag to your page, any XHR call triggered by it
   * would not be automatically added to the currently running action.
   * Calling this function allows relinking to such a subsequent XHR call (i.e., XHR actions) to its initial action.
   * The XHR callback MUST also be stopped by leaveXhrCallback.
   *
   * @param actionId ID of the action where callback belongs to
   */
  const enterXhrCallbackCallback = useCallback(enterXhrCallback, []);

  /**
   * Indicates the end of an XHR callback.
   *
   * @param actionId ID of the action the callback belongs to
   */
  const leaveXhrCallbackCallback = useCallback(leaveXhrCallback, []);

  /**
   * Reports an error object to Dynatrace. Use when you catch errors in your own application code,
   * but you also want to propagate them to Dynatrace instead of logging them yourself.
   * If errors are handled by your own application code it will stop the error from being handled
   * by the global JavaScript {https://developer.mozilla.org/en-US/docs/Web/API/GlobalEventHandlers/onerror | onerror event handler},
   * which is used by Dynatrace to automatically capture JavaScript errors.
   *
   * @param error          The error to be reported. Any browser error object is supported and if the error doesn't
   *                       have a stacktrace the RUM JavaScipt monitoring code will attempt to generate one.
   *                       Alternatively create your own object that has the following properties set: 'message',
   *                       'file', 'line', 'column', and 'stack'. The 'message' property must be provided; all other values are optional.
   * @param parentActionId parent action id. if not passed or null, error is added to current action
   */
  const reportErrorCallback = useCallback(reportError, []);

  /**
   * Sets the {@link https://www.dynatrace.com/support/help/shortlink/user-tagging#user-tagging-via-javascript-api | user tag}.
   * Use to identify individual users across different browsers, devices, and user sessions.
   *
   * @param value The name of the user. For example, use a name, userid, or your user's email address.
   */
  const identifyUserCallback = useCallback(identifyUser, []);

  return {
    addAction: addActionCallback,
    enterAction: enterActionCallback,
    leaveAction: leaveActionCallback,
    enterXhrAction: enterXhrActionCallback,
    leaveXhrAction: leaveXhrActionCallback,
    enterXhrCallback: enterXhrCallbackCallback,
    leaveXhrCallback: leaveXhrCallbackCallback,
    reportError: reportErrorCallback,
    identifyUser: identifyUserCallback,
  };
};
